import juice from 'juice';
import cssjs from 'jotform-css.js';
import { TypographyMappingMap } from '~/constants/platform_branding';

// Reverse-lookup map for repairing the `undefined:`-mangled font tokens GrapesJS emits
// (see recoverFontFamilies). The corruption joins the first font name's words and lowercases
// them (`open sans` -> `opensans`), so we key the same way and rebuild the canonical value.
const FONT_TOKEN_MAP = TypographyMappingMap.fontList.reduce((map, { name, value }) => {
    const parts = value.split(',').map((part) => part.trim()).filter(Boolean);
    const token = parts[0].replace(/\s+/g, '').toLowerCase();
    const fallback = parts.slice(1).join(', ');
    map[token] = fallback ? `'${name}', ${fallback}` : `'${name}'`;
    return map;
}, {});
export default {
    getHeadInner(html) {
        if (!html) return '';

        const dom = (new DOMParser()).parseFromString(html, 'text/html');
        const head = dom.querySelector('head');

        if (head) {
            return dom.querySelector('head').innerHTML;
        }

        return '';
    },

    getBodyInner(html) {
        if (!html) return '';

        const dom = (new DOMParser()).parseFromString(html, 'text/html');
        const body = dom.querySelector('body');
        const bogusDivs = dom.querySelectorAll('div[data-mce-bogus="all"]:empty');
        for (const bogusDiv of bogusDivs) {
            bogusDiv.style.display = 'none';
        }

        if (body) {
            return dom.querySelector('body').innerHTML;
        }

        return '';
    },

    stringToDom(html) {
        if (!html) return '';

        return (new DOMParser()).parseFromString(html, 'text/html');
    },

    buildDocument(options, hasHtmlTag = false) {
        const parts = { html: '',
            head: '',
            body: '',
...options };

        if (!hasHtmlTag) {
            return options.body;
        }

        const dom = (new DOMParser()).parseFromString(parts.html, 'text/html');
        const domBody = (new DOMParser()).parseFromString(parts.body, 'text/html');
        const bogusDivs = domBody.querySelectorAll('.mce-visual-caret-hidden, .mce-visual-caret');
        if (bogusDivs.length > 0) {
            bogusDivs.forEach((element) => {
                element.remove();
            });
        }

        dom.querySelector('head').innerHTML = parts.head;
        dom.querySelector('body').innerHTML = domBody.body.innerHTML;

        return dom.querySelector('html').outerHTML;
    },

    hasHtmlTag(html = '') {
        return html.indexOf('<html') > -1;
    },

    getHtmlParts(html = '') {
        const htmlWithNoContentBefore = this.detachHtmlFromContentBefore(html);

        return {
            contentBefore: this.detachContentBeforeHtmlTag(html),
            head: this.getHeadInner(htmlWithNoContentBefore),
            body: this.getBodyInner(htmlWithNoContentBefore),
        };
    },

    detachContentBeforeHtmlTag(html = '') {
        const htmlIndex = html.indexOf('<html');

        if (!html || htmlIndex < 0) {
            return '';
        }

        return html.substring(0, htmlIndex);
    },

    detachHtmlFromContentBefore(html = '') {
        const htmlIndex = html.indexOf('<html');

        if (!html || htmlIndex < 0) {
            return html;
        }

        return html.substring(htmlIndex, html.length - 1);
    },

    attachContentBeforeHtml(html, content) {
        return content + html;
    },

    // Repair the `undefined:`-mangled font-family values GrapesJS produces for any
    // multi-word font (e.g. `font-family: " undefined: opensans" undefined: ,sans-serif`),
    // so end users never see `undefined` as a font in the editor and the corruption is
    // healed on save. Works element-by-element over the DOM so getAttribute() returns the
    // decoded value (sidestepping the `;`-inside-`&quot;` parsing trap) and the clean
    // `data-mce-style` sibling is reachable. Three recovery layers, in order:
    //   1. Copy a clean `data-mce-style` font-family verbatim (lossless, any font).
    //   2. Reverse-lookup the mangled token against the font list (FONT_TOKEN_MAP).
    //   3. Salvage the trailing fallback family (or `sans-serif`) so nothing stays `undefined`.
    recoverFontFamilies(html, hasHtmlTag = false) {
        if (!html || html.indexOf('undefined') === -1) return html;

        const dom = this.stringToDom(html);
        const corrupted = dom.querySelectorAll('[style*="undefined"], [data-mce-style*="undefined"]');
        let changed = false;

        corrupted.forEach((element) => {
            if (this.recoverElementFontFamily(element)) changed = true;
        });

        if (!changed) return html;
        if (!hasHtmlTag) return dom.body.innerHTML;

        // documentElement.outerHTML serializes only the <html> element, dropping everything
        // before it. Re-prepend the original pre-<html> slice verbatim so the doctype AND any
        // Outlook MSO conditional comments (original email content) survive the heal.
        const htmlStart = html.search(/<html[\s>]/i);
        const beforeHtml = htmlStart > 0 ? html.slice(0, htmlStart) : '';
        return beforeHtml + dom.documentElement.outerHTML;
    },

    // Heal a single element's corrupted font-family in both `style` and `data-mce-style`,
    // applying the same recovered value to whichever attribute carries the corruption.
    recoverElementFontFamily(element) {
        const style = element.getAttribute('style') || '';
        const mceStyle = element.getAttribute('data-mce-style') || '';
        const styleCorrupted = /font-family:[^;]*undefined/i.test(style);
        const mceCorrupted = /font-family:[^;]*undefined/i.test(mceStyle);
        if (!styleCorrupted && !mceCorrupted) return false;

        const cleanValue = this.resolveCleanFontValue(style, mceStyle);
        if (!cleanValue) return false;

        if (styleCorrupted) element.setAttribute('style', this.replaceFontFamilyValue(style, cleanValue));
        if (mceCorrupted) element.setAttribute('data-mce-style', this.replaceFontFamilyValue(mceStyle, cleanValue));
        return true;
    },

    // Determine the clean font-family value to write, preferring a lossless source.
    resolveCleanFontValue(style, mceStyle) {
        const mceFont = (mceStyle.match(/font-family:\s*([^;]+)/i) || [])[1];
        if (mceFont && !/undefined/i.test(mceFont)) return mceFont.trim();

        const styleFont = (style.match(/font-family:\s*([^;]+)/i) || [])[1];
        if (styleFont && !/undefined/i.test(styleFont)) return styleFont.trim();

        return this.reverseLookupFont(styleFont || mceFont || '');
    },

    // Layers 2 + 3: map the mangled token back to its canonical value, else salvage the
    // trailing fallback family so the result is always a valid, undefined-free declaration.
    reverseLookupFont(corruptedValue) {
        const token = (corruptedValue.match(/undefined:\s*([a-z0-9]+)/i) || [])[1];
        if (token && FONT_TOKEN_MAP[token.toLowerCase()]) {
            return FONT_TOKEN_MAP[token.toLowerCase()];
        }

        const fallback = (corruptedValue.match(/,\s*([a-z0-9 -]+?)\s*$/i) || [])[1];
        return (fallback && fallback.trim()) || 'sans-serif';
    },

    // Swap every CORRUPTED font-family declaration for cleanValue, leaving clean sibling
    // declarations untouched. The pattern requires `undefined` so a clean font-family is never
    // matched, and the `g` flag heals all corrupted declarations regardless of order or count
    // (a non-global, position-based replace would heal only the first and miss a later one).
    replaceFontFamilyValue(attributeValue, cleanValue) {
        return attributeValue.replace(/font-family:\s*[^;]*undefined[^;]*/gi, `font-family: ${cleanValue}`);
    },

    formatDocument(options) {
        options = {
            html: '',
            head: '',
            body: '',
            css: '',
            contentBefore: '',
            keepElementIds: false,
            hasHtmlTag: false,
            inline: true,
            useStyleTags: true,
            prependToBody: null,
            filterCssRules: false,
            ...options,
        };

        if (options.hasHtmlTag) {
            options.contentBefore = this.regexRemoveStyleTag(options.contentBefore);
        }

        if (typeof options.filterCssRules === 'function') {
            // cssjs round-trip (parse -> filter -> reserialize), NOT Liquid-masked. Balanced
            // Liquid output expressions ({{ x }}) survive this intact — even next to an @media
            // block (verified) — which is why email dynamic colors persist here (DEF-3186).
            // {% %} control-flow or a literal brace inside a Liquid string would be mangled;
            // see bundles/email/styles/baseline_stripper for the sites that Liquid-mask.
            const cssJson = this.cssToJson(options.css);
            const newCss = this.filterCssRules(cssJson, options.filterCssRules);
            const parser = new cssjs.cssjs();
            options.css = parser.getCSSForEditor(newCss);
        }

        options.contentBefore = options.contentBefore.trim();

        options.html = this.buildDocument(options, options.hasHtmlTag);

        if (options.css && options.inline) {
            if (options.useStyleTags) {
                options.html = this.inlineDocument(options.html, options.css);
            } else {
                options.html = this.inlineBlock(options.body, options.css);
            }
        }

        options.html = this.fixColumnPadding(options.html, options.hasHtmlTag);

        if (options.keepElementIds !== false) {
            options.html = this.stripElementIdsExcept(options.html, options.keepElementIds, options.hasHtmlTag);
        }

        if (!options.inline && options.css && !options.hasHtmlTag) {
            options.html = `
                <style>
                    ${options.css}
                </style>

                ${options.html}
            `;
        }

        if (options.prependToBody != null) {
            options.html = this.prependToBody(options.html, options.prependToBody);
        }

        return this.attachContentBeforeHtml(options.html, options.contentBefore);
    },

    regexRemoveStyleTag(content = '') {
        const regex = /((<style>)|(<style type=.+))((\s+)|(\S+)|(\r+)|(\n+))(.+)((\s+)|(\S+)|(\r+)|(\n+))(<\/style>)/g;
        return content.replace(regex, '');
    },

    stripUngatedOnlyScreen(css) {
        if (!css) return '';
        // Match @media only screen { ... } — the un-gated GrapesJS artifact.
        // \s*\{ fails for "@media only screen and (...)" because "and" is not
        // whitespace, so properly-gated rules are never matched.
        // Handles one level of nested braces, which covers Foundation's mobile rules.
        return css.replace(/@media\s+only\s+screen\s*\{(?:[^{}]|\{[^{}]*\})*\}/gi, '');
    },

    // Splits CSS into @media blocks and everything else. Called before juice so
    // media rules bypass the cssjs serializer (which can mangle complex values)
    // and land verbatim in the saved <style>.
    extractMediaBlocks(css) {
        if (!css) return { mediaBlocks: '', remainingCss: '' };
        const blocks = [];
        const remaining = css.replace(/@media[^{]*\{(?:[^{}]|\{[^{}]*\})*\}/gi, (match) => {
            blocks.push(match);
            return '';
        });
        return {
            mediaBlocks: blocks.join('\n'),
            remainingCss: remaining,
        };
    },

    filterCssRules(arr, callback) {
        return arr.map((item) => {
            if (item.subStyles) {
                item.subStyles = item.subStyles.map((sub) => {
                    if (sub.rules) {
                        sub.rules = sub.rules.filter(callback);
                    }
                    return sub;
                });
            } else if (item.rules) {
                item.rules = item.rules.filter(callback);
            }

            return item;
        });
    },

    formatBlockDocument(options) {
        options = {
            contentBefore: '',
            html: '',
            head: '',
            body: '',
            ...options,
        };

        // this fixes the block preview html so it can be properly rendered to a preview image in pdfReactor
        const dom = (new DOMParser()).parseFromString(options.body, 'text/html');
        const imgs = dom.querySelectorAll('center > img');

        if (imgs.length) {
            for (let i = imgs.length - 1; i >= 0; i--) {
                imgs[i].parentElement.style.width = null;
                imgs[i].parentElement.style.minWidth = null;
            }

            options.body = dom.querySelector('body').innerHTML;
        }

        return this.attachContentBeforeHtml(
            this.buildDocument(options, true),
            options.contentBefore,
        );
    },

    inlineDocument(html, css) {
        return juice(this.getHtmlWithStyleTag(html, css), {
            applyAttributesTableElements: false,
            applyHeightAttributes: false,
            applyWidthAttributes: false,
        });
    },

    inlineBlock(html, css) {
        return juice.inlineContent(html, css, {
            applyAttributesTableElements: false,
            applyHeightAttributes: false,
            applyWidthAttributes: false,
        });
    },

    getHtmlWithStyleTag(html, css) {
        // Parse, drop all existing <style> blocks, ensure a viewport meta
        // exists, and insert a fresh <style> immediately after it.
        const doctypeMatch = html.match(/<!doctype[^>]*>/i);
        const doctype = doctypeMatch ? `${doctypeMatch[0]}\n` : '';

        const dom = this.removeStyleTags(html);
        const head = dom.querySelector('head');
        if (!head) {
            return html;
        }

        let viewport = dom.querySelector('meta[name="viewport"]');
        if (!viewport) {
            viewport = dom.createElement('meta');
            viewport.setAttribute('name', 'viewport');
            viewport.setAttribute('content', 'width=device-width');
            head.insertBefore(viewport, head.firstChild);
        }

        const style = dom.createElement('style');
        style.textContent = css;
        viewport.parentNode.insertBefore(style, viewport.nextSibling);

        return doctype + dom.documentElement.outerHTML;
    },

    prefixHtmlWithStyleTag(html, css) {
        return ` <style>${css}</style> ${html}`;
    },

    stripElementIdsExcept(html, keepIds = [], hasHtmlTag = false) {
        const dom = (new DOMParser()).parseFromString(html, 'text/html');
        const elements = dom.querySelectorAll('*');

        // might be a good place to strip out gjs-selected class
        // strip out data-highlightable

        for (let i = elements.length - 1; i >= 0; i--) {
            if (keepIds.includes(elements[i].getAttribute('id'))) {
                continue;
            } else {
                elements[i].removeAttribute('id');
            }
        }

        if (hasHtmlTag) {
            return dom.documentElement.outerHTML;
        }

        return dom.body.innerHTML;
    },

    extractStyles(html) {
        if (!html) {
            return '';
        }
        let css = '';
        const dom = this.stringToDom(html);
        // workaround .forEach() missing on NodeList in IE11
        // TODO: consider a polyfill for this
        const styleTags = Array.prototype.slice.call(dom.querySelectorAll('style'), 0);
        styleTags.forEach((style) => {
            css += style.innerHTML;
        });

        return css;
    },

    removeStyleTags(html) {
        const dom = this.stringToDom(html);

        // workaround .forEach() missing on NodeList in IE11
        // TODO: consider a polyfill for this
        const styleTags = Array.prototype.slice.call(dom.querySelectorAll('style'), 0);

        styleTags.forEach((style) => {
            // Remove doesn't exist in node elements in IE11
            // TODO: Consider a polyfill for this
            if (typeof style.remove === 'function') {
                style.remove();
              } else {
                style.parentNode.removeChild(style);
              }
        });

        return dom;
    },

    replaceStyles(html, css, contentBefore = '') {
        if (!html) {
            return '';
        }
        if (!css && !contentBefore) {
            return html;
        }

        const body = this.removeStyleTags(html).querySelector('body').innerHTML;

        return `
            ${contentBefore}

            <style>
                ${css}
            </style>

            ${body}
        `;
    },

    cssToJson(css) {
        const parser = new cssjs.cssjs();
        return parser.parseCSS(css);
    },

    filterCssIds(css, compare = []) {
        const parser = new cssjs.cssjs();
        const json = parser.parseCSS(css);
        compare = compare.map((item) => item.replace('#', ''));

        const matched = json.filter((item) => compare.includes(item.selector.replace('#', '')));

        return parser.getCSSForEditor(matched);
    },

    removeAllAttr(el, attrs = []) {
        const elements = el.querySelectorAll('*');

        for (var i = elements.length - 1; i >= 0; i--) {
            attrs.forEach((item) => {
                elements[i].removeAttribute(item);
            });
        }

        return el;
    },

    iterateCss(arr, callback) {
        arr.forEach((item) => {
            if (item.rules) {
                item.rules.forEach(callback);
            } else if (item.subStyles) {
                item.subStyles.forEach((sub) => {
                    sub.rules.forEach(callback);
                });
            }
        });

        return arr;
    },

    getPxPerMm() {
        const mm = 100000;
        let $el = $('.js-mm');

        if (!$el.length) {
            $('body').append(`<div style="height: ${mm}mm;" class="js-mm"></div>`);
            $el = $('.js-mm');
        }

        return $el.height() / mm;
    },

    // expects array from css.js parser.parseCSS
    pxToMm(selectors) {
        const pxPerMm = this.getPxPerMm();

        return this.iterateCss(selectors, (rule) => {
            if (rule.value.indexOf('px') > -1) {
                rule.value = rule.value.split(' ').map((item) => {
                    if (this.isTranslateRule(item)) {
                        const currentValue = this.getRuleValue(item);
                        const updatedValue = currentValue.split(',').map((item) => `${parseFloat(item.trim()) / pxPerMm}mm`).join(', ');

                    return item.replace(currentValue, updatedValue);
                    }
                        return `${parseFloat(item) / pxPerMm}mm`;
                }).join(' ');
            }
        });
    },

    // Check if CSS rule is translate type
    isTranslateRule(value) {
        return value.indexOf('translate') > -1;
    },

    // Get css value enclosed within parenthesis
    getRuleValue(value) {
        return value.match(/\(([^)]+)\)/)[1];
    },

    /**
     * This parses a grapes style object to workaround
     * a grapes parsing bug with data url's
     */
    parseGrapesBackgroundImage(value) {
        if (!value['background-image']) return;

        let backgroundImage = value['background-image'];

        if (/url\(["']?data/.test(backgroundImage)) {
            const base64String = Object.keys(value).filter((key) => /^base64/.test(key));
            backgroundImage = `${value['background-image']};${base64String}`;
        }

        return this.parseBackgroundImage(backgroundImage);
    },

    parseBackgroundImage(value) {
        if (!value) return;

        return value.replace(/(?:^url\(["']?|["']?\)$)/g, '');
    },

    prependToBody(html, prependObject) {
        const prependHtmlString = prependObject.html || '';
        const prependSelector = prependObject.selector || '';
        const dom = this.stringToDom(html);

        const existingElement = dom.querySelector(prependSelector);

        if (existingElement != null) {
            existingElement.parentNode.removeChild(existingElement);
        }

        const body = dom.querySelector('body');

        body.insertAdjacentHTML('afterbegin', prependHtmlString);
        return dom.querySelector('html').outerHTML;
    },

    // Reconcile two requirements that conflict for horizontal column padding:
    //   1. Desktop must render exactly what the user set (any value, including < 16px).
    //   2. Mobile (Foundation's `@media only screen` rule) forces a 16px L/R gutter on
    //      stacked columns — desired as a *floor* for text, but it also wrongly *caps*
    //      larger user values and would erase full-width image bleed.
    //
    // Foundation's rule targets the outer column th and sets L/R to `16px !important`.
    // That is a SET, not a clamp: it raises values < 16 (good) but also lowers values
    // > 16 (bad) and overrides explicit 0 (kills bleed). We resolve this per horizontal
    // side, leaning on Foundation where it does the right thing and only intervening
    // where it doesn't:
    //
    //   value > 16  → move to the inner th (Foundation can't reach it); zero the outer
    //                 side with !important so Foundation's rule is blocked. Desktop &
    //                 mobile both render the user's value.
    //   0 < v ≤ 16  → leave on the outer with NO !important. Desktop renders v; Foundation
    //                 floors it to 16 on mobile. No JavaScript floor needed.
    //   v == 0, flush (both sides 0) → outer side = 0 !important to block Foundation, so
    //                 full-width image bleed survives on mobile.
    //   v == 0, single side → leave on the outer (no !important): desktop 0, Foundation
    //                 floors to 16 on mobile (matches shipped behavior).
    //
    // Vertical padding is never touched by Foundation, so it always stays on the outer.
    fixColumnPadding(html, hasHtmlTag = false) {
        if (!html) return html;

        const dom = this.stringToDom(html);
        const columnThs = html.includes('data-type="column"')
            ? dom.querySelectorAll('th[data-type="column"]')
            : [];

        if (!columnThs.length) return html;

        const zeros = ['',
'0',
'0px',
'0em',
'0rem',
'0pt'];
        const isZero = (v) => zeros.includes((v || '').trim());
        // Assumes px values — non-px units (em, rem) treated as their numeric magnitude.
        const exceedsFloor = (v) => !isZero(v) && (parseFloat(v) || 0) > 16;
        let changed = false;

        columnThs.forEach((outerTh) => {
            // Resolve the user's intended padding from the raw style string. A longhand
            // expresses an explicit user edit and must win over a `padding` shorthand even
            // when juice appended the shorthand later (which would otherwise override it in
            // the cascade). Sides defined only by the shorthand fall back to its expansion.
            const padding = this.resolveIntendedPadding(outerTh.getAttribute('style') || '');

            const anyNonZero = Object.values(padding).some((v) => !isZero(v));
            if (!anyNonZero) return; // fully-flush/empty column: leave for Foundation to floor.

            const moveLeft = exceedsFloor(padding.left);
            const moveRight = exceedsFloor(padding.right);
            const flush = isZero(padding.left) && isZero(padding.right);
            const hadShorthand = /(?:^|;)\s*padding\s*:/.test(outerTh.getAttribute('style') || '');

            // Mutate only when there is something to correct: a side to protect on the
            // inner, a flush column to pin with !important, or a stale shorthand to strip.
            if (!moveLeft && !moveRight && !flush && !hadShorthand) return;

            // Per-side disposition for the outer th.
            const outerSide = (side) => {
                const v = padding[side];
                if (exceedsFloor(v)) return { value: '0px', important: true }; // protected on inner
                if (flush) return { value: '0px', important: true }; // bleed: block Foundation
                return { value: v || '0px', important: false }; // Foundation floors mobile
            };

            const left = outerSide('left');
            const right = outerSide('right');

            outerTh.style.removeProperty('padding');
            outerTh.style.setProperty('padding-top', padding.top || '0px');
            outerTh.style.setProperty('padding-bottom', padding.bottom || '0px');
            outerTh.style.removeProperty('padding-left');
            outerTh.style.removeProperty('padding-right');
            outerTh.style.setProperty('padding-left', left.value, left.important ? 'important' : '');
            outerTh.style.setProperty('padding-right', right.value, right.important ? 'important' : '');

            // Only touch the inner element when a side actually needs protecting there.
            if (moveLeft || moveRight) {
                const innerEl = outerTh.querySelector('table > tbody > tr > th, table > tbody > tr > td');
                if (innerEl) {
                    ['padding',
'padding-top',
'padding-right',
'padding-bottom',
'padding-left']
                        .forEach((prop) => innerEl.style.removeProperty(prop));
                    innerEl.style.paddingTop = '0px';
                    innerEl.style.paddingBottom = '0px';
                    innerEl.style.paddingLeft = moveLeft ? padding.left : '0px';
                    innerEl.style.paddingRight = moveRight ? padding.right : '0px';
                }
            }

            changed = true;
        });

        // Legacy heal: strip stale top/bottom !important left by an older fix on columns
        // we didn't otherwise touch (Foundation never targets vertical padding).
        columnThs.forEach((outerTh) => {
            if (outerTh.style.getPropertyPriority('padding-top') === 'important') {
                outerTh.style.setProperty('padding-top', outerTh.style.paddingTop || '0px');
                changed = true;
            }
            if (outerTh.style.getPropertyPriority('padding-bottom') === 'important') {
                outerTh.style.setProperty('padding-bottom', outerTh.style.paddingBottom || '0px');
                changed = true;
            }
        });

        if (!changed) return html;

        return hasHtmlTag ? dom.documentElement.outerHTML : dom.body.innerHTML;
    },

    // Inverse of fixColumnPadding, applied when LOADING an email into the editor.
    // fixColumnPadding moves >16px horizontal padding onto the inner th and zeroes the
    // outer column on save. GrapesJS's style panel reads the outer column, so it shows 0
    // for those sides — the user sees "my padding didn't stick" even though it renders.
    // This lifts the inner padding back onto the outer column (summing, since only one of
    // outer/inner is non-zero per side after fixColumnPadding) and clears the inner, so
    // the panel shows the real value and the canvas renders identically. On the next save
    // fixColumnPadding re-derives the saved shape, so stored HTML is unchanged.
    //
    // Triggered ONLY when the inner horizontal padding exceeds 16px — the unmistakable
    // signature of a fixColumnPadding move. Template-default inner padding (≤16px, e.g.
    // Foundation's 16px gutter) is left exactly in place, so default columns and their
    // mobile rendering are never altered.
    restoreColumnPaddingForEditor(html, hasHtmlTag = false) {
        if (!html) return html;

        const dom = this.stringToDom(html);
        const columnThs = html.includes('data-type="column"')
            ? dom.querySelectorAll('th[data-type="column"]')
            : [];

        if (!columnThs.length) return html;

        let changed = false;

        columnThs.forEach((outerTh) => {
            const innerEl = outerTh.querySelector('table > tbody > tr > th, table > tbody > tr > td');
            if (!innerEl) return;

            // Only lift columns whose inner horizontal padding exceeds 16px — values that
            // could only have gotten there via a fixColumnPadding move. Anything ≤16px
            // (template defaults) is left untouched to avoid changing default rendering.
            const innerLeft = parseFloat(innerEl.style.paddingLeft) || 0;
            const innerRight = parseFloat(innerEl.style.paddingRight) || 0;
            if (innerLeft <= 16 && innerRight <= 16) return;

            // Capture the combined padding per side BEFORE mutating (reading after
            // removeProperty would yield 0). Only one of outer/inner is non-zero per side
            // after fixColumnPadding, so the sum is the value the user originally set.
            // Summing all four sides also preserves vertical padding that older,
            // pre-per-side saves may have parked on the inner element.
            const sumSide = (side) => {
                const outer = parseFloat(outerTh.style[`padding${side}`]) || 0;
                const inner = parseFloat(innerEl.style[`padding${side}`]) || 0;
                return `${outer + inner}px`;
            };
            const sums = {
                top: sumSide('Top'),
                right: sumSide('Right'),
                bottom: sumSide('Bottom'),
                left: sumSide('Left'),
            };

            // Move the combined padding onto the outer column (no !important — that only
            // matters in the saved/sent email, which fixColumnPadding re-applies on save).
            outerTh.style.removeProperty('padding');
            ['padding-top', 'padding-right', 'padding-bottom', 'padding-left']
                .forEach(prop => outerTh.style.removeProperty(prop));
            outerTh.style.setProperty('padding-top', sums.top);
            outerTh.style.setProperty('padding-right', sums.right);
            outerTh.style.setProperty('padding-bottom', sums.bottom);
            outerTh.style.setProperty('padding-left', sums.left);

            // Clear the inner so the canvas doesn't double the padding.
            ['padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left']
                .forEach(prop => innerEl.style.removeProperty(prop));
            innerEl.style.paddingTop = '0px';
            innerEl.style.paddingRight = '0px';
            innerEl.style.paddingBottom = '0px';
            innerEl.style.paddingLeft = '0px';

            changed = true;
        });

        if (!changed) return html;

        return hasHtmlTag ? dom.documentElement.outerHTML : dom.body.innerHTML;
    },

    // Resolve the four padding sides a user intended from a raw inline style string.
    // Applies any `padding` shorthand first (last one wins), then lets explicit
    // `padding-{side}` longhands override — regardless of source order — because a
    // longhand represents a deliberate edit while a trailing shorthand is typically a
    // juice/serialization artifact. Returns plain strings ('' when a side is unset).
    resolveIntendedPadding(rawStyle) {
        const sides = ['top',
'right',
'bottom',
'left'];
        const result = {
            top: '', right: '', bottom: '', left: '',
        };

        // Expand the last `padding:` shorthand (1–4 values) into all four sides.
        const shorthandMatches = rawStyle.match(/(?:^|;)\s*padding\s*:\s*[^;!]+/gi);
        if (shorthandMatches && shorthandMatches.length) {
            const last = shorthandMatches[shorthandMatches.length - 1];
            const value = last.slice(last.indexOf(':') + 1).trim();
            const p = value.split(/\s+/).filter(Boolean);
            const [top,
right = top,
bottom = top,
left = right] = p;
            Object.assign(result, {
                top, right, bottom, left,
            });
        }

        // Explicit longhands override the shorthand (last occurrence per side wins).
        sides.forEach((side) => {
            const matches = rawStyle.match(new RegExp(`(?:^|;)\\s*padding-${side}\\s*:\\s*([^;!]+)`, 'gi'));
            if (matches && matches.length) {
                const last = matches[matches.length - 1];
                result[side] = last.slice(last.indexOf(':') + 1).trim();
            }
        });

        return result;
    },

    // Heal pass for emails corrupted by an earlier version of the column padding fix.
    // The corruption signature: padding moved from outer th to inner th with a 16px L/R
    // floor applied to columns the user intended to be flush. Three signals identify it
    // unambiguously:
    //   1. The row's tr or row table has class="collapse" (Foundation Inky's marker for
    //      a no-gutter row — user-declared intent for flush columns).
    //   2. The column contains an <img> descendant. Text and button content in collapse
    //      rows traditionally have template-default inner padding that should NOT be
    //      stripped; only image columns lost their bleed when the floor applied.
    //   3. The inner th's padding-left AND padding-right are exactly "16px" — the floor
    //      value. Foundation stock never writes inline L/R padding on inner th, so this
    //      combination is the unmistakable fingerprint of the bug.
    // When healed, also re-stretch any image whose width matches the floor's footprint
    // (container_width - 32) back to the container's full width.
    //
    // Intentionally separated from fixColumnPadding so it can be opted into per-run
    // (e.g. by the bulk resave tool) without modifying every save.
    healCollapsePaddingFloor(html, hasHtmlTag = false) {
        if (!html) return html;

        const dom = this.stringToDom(html);
        const columnThs = html.includes('data-type="column"')
            ? dom.querySelectorAll('th[data-type="column"]')
            : [];

        if (!columnThs.length) return html;

        const isExactly16px = (v) => (v || '').trim() === '16px';
        let changed = false;

        columnThs.forEach((outerTh) => {
            const tr = outerTh.parentElement;
            const rowTable = tr && tr.closest('table[data-type="row"]');
            const trHasCollapse = tr && tr.classList && tr.classList.contains('collapse');
            const tableHasCollapse = rowTable && rowTable.classList && rowTable.classList.contains('collapse');
            if (!trHasCollapse && !tableHasCollapse) return;

            // Image gate: only heal columns containing an image. Text and button content
            // in collapse rows have template-default inner padding that's part of their
            // long-standing rendering contract — clearing it would change how those
            // columns render on desktop, which is not the bug we're fixing here.
            const img = outerTh.querySelector('img');
            if (!img) return;

            const innerEl = outerTh.querySelector(
                'table > tbody > tr > th, table > tbody > tr > td',
            );
            if (!innerEl) return;
            if (!isExactly16px(innerEl.style.paddingLeft) || !isExactly16px(innerEl.style.paddingRight)) return;

            innerEl.style.removeProperty('padding-left');
            innerEl.style.removeProperty('padding-right');
            changed = true;

            // Image resize: if the img's width matches the post-fix "shrunk" footprint
            // (container_width - 32), restore it to container width.
            const container = outerTh.closest('table.container');
            if (!container) return;
            const containerWidth = parseInt(container.style.width, 10);
            if (!containerWidth) return;
            const imgWidth = parseInt(img.getAttribute('width'), 10);
            if (imgWidth !== containerWidth - 32) return;
            img.setAttribute('width', String(containerWidth));
            img.style.setProperty('width', `${containerWidth}px`);

            // Preserve aspect ratio: scale the numeric height attribute proportionally to
            // the new width (kept for Outlook, which is non-responsive). The inline style
            // height is set to `auto`, never a fixed px value — a fixed inline height does
            // not scale with the responsive width on mobile and stretches the image
            // vertically on iOS Mail. Only touched when an explicit height existed; an
            // image with no height is already responsive and is left alone.
            const oldHeight = parseFloat(img.getAttribute('height'));
            if (!isNaN(oldHeight) && imgWidth > 0) {
                const newHeight = (oldHeight * containerWidth) / imgWidth;
                img.setAttribute('height', String(newHeight));
                img.style.setProperty('height', 'auto');
            }
        });

        if (!changed) return html;
        return hasHtmlTag ? dom.documentElement.outerHTML : dom.body.innerHTML;
    },

    // Escape the quote characters inside font-family declarations to HTML
    // entities so they can't terminate the enclosing attribute on the next parse.
    // A multi-word font value (font-family: "open sans") carries quotes, and the value
    // lives in BOTH style (delimited by ") and data-mce-style (delimited by '), so no
    // literal quote is safe in both — swapping " for ' only moves the truncation. Escaping
    // is delimiter-agnostic: &quot; / &#39; can't close either attribute, and the browser
    // decodes them back to the correct value on parse.
    //
    // Bounded by the trailing ';' so the match stays inside one declaration (font-family
    // values never contain ';'). The class also excludes '<' and '>' so that if a paste
    // has font-family as the last declaration with no trailing ';', the match stops at the
    // tag boundary and becomes a safe no-op rather than running past the attribute's closing
    // quote into the next element. Idempotent: an already-escaped entity has no raw quote.
    //
    // The escape is scoped to the inside of a tag (the `<...>` matched by the outer regex),
    // because that is the only context where the entity is decoded back to a quote on parse.
    // The two other places font-family can appear are deliberately left untouched: CSS inside
    // a <style> block is raw text handed to the CSS parser, which does NOT decode entities, so
    // escaping there produces an invalid declaration the engine drops; and a font-family
    // string in raw body text is rendered as content, so escaping it would only rewrite the
    // user's original bytes (and break literal DB searches) for no rendering benefit. Both
    // live between tags, never inside `<...>`, so the outer match skips them for free.
    normalizeFontFamilyQuotes(html) {
        if (!html) return html;

        return html.replace(/<[^>]+>/g, (tag) => tag.replace(
            /font-family\s*:[^;<>]*;/gi,
            (declaration) => declaration.replace(/"/g, '&quot;').replace(/'/g, '&#39;'),
        ));
    },

    // Remove the dead "style shrapnel" left on elements whose style or data-mce-style was
    // shredded into boolean attributes by an earlier quote-truncation (e.g.
    // `open="" sans',="" overflow-wrap:="" ...`). The original values are already lost, but
    // the element's intact `style` still drives rendering, and TinyMCE rebuilds a clean
    // `data-mce-style` from `style` once the corpse is gone.
    //
    // Shredding produces a CONTIGUOUS run of empty-valued attributes (a valued attribute
    // breaks the run, and DOM order mirrors source order). A run is debris only if at least
    // one of its names carries CSS punctuation (`: ; ( ) , % !` or a quote) — characters
    // that can't occur in a real attribute name. When it is, the whole run is dropped, which
    // also clears the bare-word fragments in it (the leading `open` of `"open sans"`, etc.)
    // without needing an open-ended font allowlist. An isolated empty attribute (a lone
    // boolean attr, a length-1 run with no CSS punctuation) is left untouched.
    //
    // Two classes of empty attribute must survive even when contiguous with debris, because
    // the punctuation test can't tell them apart from a value fragment: any `data-*`, and the
    // legitimately-empty HTML attributes in `keepEmptyAttribute` below (the `alt=""`
    // decorative-image marker and the standard boolean attributes). Unlike font names, that is
    // a closed, spec-defined set, so exempting it by name is the same kind of carve-out as
    // `data-*`, not the open-ended allowlist this heuristic exists to avoid. Without it, a real
    // empty attribute sitting inside a punctuated run is swept as collateral, silently dropping
    // (e.g.) an image's alt during a heal that is meant only to remove dead style debris.
    stripStyleShrapnel(html, hasHtmlTag = false) {
        if (!html) return html;

        const keepEmptyAttribute = /^(?:alt|nowrap|disabled|checked|selected|readonly|required|multiple|hidden)$/i;

        const dom = this.stringToDom(html);
        let changed = false;

        dom.querySelectorAll('*').forEach((element) => {
            let run = [];

            const flushRun = () => {
                if (run.some((name) => /[:;(),%!']/.test(name))) {
                    run.forEach((name) => {
                        const lower = name.toLowerCase();
                        if (!lower.startsWith('data-') && !keepEmptyAttribute.test(lower)) {
                            element.removeAttribute(name);
                            changed = true;
                        }
                    });
                }
                run = [];
            };

            element.getAttributeNames().forEach((name) => {
                if (element.getAttribute(name) === '') {
                    run.push(name);
                } else {
                    flushRun();
                }
            });
            flushRun();
        });

        if (!changed) return html;
        return hasHtmlTag ? dom.documentElement.outerHTML : dom.body.innerHTML;
    },
};
